import React from 'react';
import { Link } from 'react-router-dom';
import { 
    PieChart, Search, Target, Activity, Eye, CalendarCheck, BarChart3, Bot,
    ArrowRight, ShieldCheck, Rocket, Scale, Hourglass, BookOpen 
} from 'lucide-react';
import { useTranslation } from 'react-i18next'; // IMPORT I18N

const LandingPage = () => {
  const { t } = useTranslation(); // INIT I18N

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      
      {/* HERO SECTION */}
      <header className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden bg-white border-b border-slate-100">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-indigo-50 rounded-full blur-3xl opacity-60 -z-10"></div>
        <div className="absolute right-0 top-20 w-72 h-72 bg-emerald-50 rounded-full blur-3xl opacity-40 -z-10"></div>
        
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <Link to="/blog" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-8 border border-emerald-100 hover:bg-emerald-100 hover:scale-105 transition-all cursor-pointer shadow-sm">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span> {t('landing.hero_badge')}
          </Link>
          
          <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-8 leading-tight text-slate-900">
            {t('landing.hero_title1')} <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-emerald-500">{t('landing.hero_title2')}</span>
          </h1>
          
          <p className="text-xl text-slate-500 mb-10 max-w-2xl mx-auto leading-relaxed font-medium">
            {t('landing.hero_subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="w-full sm:w-auto px-8 py-4 text-lg rounded-2xl bg-slate-900 text-white font-black shadow-xl shadow-slate-900/20 hover:bg-slate-800 hover:-translate-y-1 transition-all flex items-center justify-center gap-2 group">
              {t('landing.btn_register')} <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform"/>
            </Link>
            <Link to="/analyses" className="w-full sm:w-auto px-8 py-4 text-lg rounded-2xl bg-white border border-slate-200 text-slate-700 font-bold hover:bg-slate-50 hover:shadow-sm transition-all flex items-center gap-2 justify-center">
               <BookOpen size={20} className="text-slate-400"/> {t('landing.btn_analyses')}
            </Link>
          </div>
        </div>

        {/* Faux Dashboard Visuel (Mockup Moderne) */}
        <div className="max-w-5xl mx-auto mt-20 px-6 hidden md:block">
            <div className="bg-slate-900 rounded-t-3xl p-4 shadow-2xl border-x border-t border-slate-800 relative overflow-hidden">
                <div className="flex gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                </div>
                <div className="grid grid-cols-3 gap-4 opacity-80">
                    <div className="h-32 bg-slate-800 rounded-2xl border border-slate-700 flex flex-col justify-end p-4">
                         <div className="w-1/3 h-2 bg-indigo-500/40 rounded-full mb-2"></div>
                         <div className="w-1/2 h-2 bg-emerald-500/40 rounded-full"></div>
                    </div>
                    <div className="h-32 bg-slate-800 rounded-2xl border border-slate-700 flex flex-col justify-center items-center gap-2">
                        <Activity size={32} className="text-indigo-400"/>
                        <div className="w-1/2 h-2 bg-indigo-500/20 rounded-full"></div>
                    </div>
                    <div className="h-32 bg-slate-800 rounded-2xl border border-slate-700 flex flex-col justify-between p-4">
                        <div className="w-8 h-8 rounded-full bg-amber-500/20"></div>
                        <div className="w-3/4 h-2 bg-slate-600 rounded-full"></div>
                    </div>
                </div>
                <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-slate-50 to-transparent"></div>
            </div>
        </div>
      </header>

      {/* STRATÉGIE CQP */}
      <section className="py-24 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white rounded-full blur-3xl opacity-60 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="subtitle mb-2 block text-indigo-500 font-bold uppercase tracking-wider text-xs">{t('landing.method_subtitle')}</span>
                <h2 className="title-page text-3xl md:text-4xl font-black mb-6">{t('landing.method_title')}</h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                    {t('landing.method_desc')}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <div className="card-panel bg-white p-8 rounded-3xl border-t-4 border-indigo-500 shadow-sm hover:-translate-y-2 transition-transform duration-300">
                    <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 mb-6"><ShieldCheck size={32} strokeWidth={2.5}/></div>
                    <h3 className="text-xl font-black text-slate-900 mb-3">{t('landing.method_step1_title')}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">{t('landing.method_step1_desc')}</p>
                </div>
                <div className="card-panel bg-white p-8 rounded-3xl border-t-4 border-emerald-500 shadow-sm hover:-translate-y-2 transition-transform duration-300">
                    <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 mb-6"><Rocket size={32} strokeWidth={2.5}/></div>
                    <h3 className="text-xl font-black text-slate-900 mb-3">{t('landing.method_step2_title')}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">{t('landing.method_step2_desc')}</p>
                </div>
                <div className="card-panel bg-white p-8 rounded-3xl border-t-4 border-amber-500 shadow-sm hover:-translate-y-2 transition-transform duration-300">
                    <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 mb-6"><Scale size={32} strokeWidth={2.5}/></div>
                    <h3 className="text-xl font-black text-slate-900 mb-3">{t('landing.method_step3_title')}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">{t('landing.method_step3_desc')}</p>
                </div>
            </div>

            <div className="card-dark p-8 md:p-10 flex flex-col md:flex-row items-center gap-8 bg-slate-900 rounded-3xl text-white relative overflow-hidden">
                <div className="absolute right-0 top-0 w-64 h-64 bg-indigo-500 rounded-full blur-[80px] opacity-20"></div>
                <div className="bg-white/10 p-4 rounded-full text-indigo-300 shrink-0"><Hourglass size={40} /></div>
                <div className="relative z-10">
                    <h4 className="text-xl font-bold mb-2">{t('landing.method_horizon_title')}</h4>
                    <p className="text-slate-300 text-sm leading-relaxed">{t('landing.method_horizon_desc')}</p>
                </div>
            </div>
        </div>
      </section>

      {/* OUTILS FUSIONNÉS */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black mb-4 text-slate-900">{t('landing.tools_title')}</h2>
            <p className="text-slate-500">{t('landing.tools_desc')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard 
              icon={PieChart} title={t('landing.tool1_title')} color="sky"
              desc={t('landing.tool1_desc')} 
            />
            <FeatureCard 
              icon={Search} title={t('landing.tool2_title')} color="blue"
              desc={t('landing.tool2_desc')} 
            />
            <FeatureCard 
              icon={Target} title={t('landing.tool3_title')} color="emerald"
              desc={t('landing.tool3_desc')} 
            />
            <FeatureCard 
              icon={Activity} title={t('landing.tool4_title')} color="pink"
              desc={t('landing.tool4_desc')} 
            />
            <FeatureCard 
              icon={Eye} title={t('landing.tool5_title')} color="rose"
              desc={t('landing.tool5_desc')} 
            />
            <FeatureCard 
              icon={CalendarCheck} title={t('landing.tool6_title')} color="amber"
              desc={t('landing.tool6_desc')} 
            />
            <FeatureCard 
              icon={BarChart3} title={t('landing.tool7_title')} color="purple"
              desc={t('landing.tool7_desc')} 
            />
            <FeatureCard 
              icon={Bot} title={t('landing.tool8_title')} color="indigo"
              desc={t('landing.tool8_desc')} 
            />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold">K</div>
            <span className="font-bold">{t('landing.footer_rights')}</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm font-bold text-slate-400">
            <Link to="/analyses" className="hover:text-white transition-colors">{t('landing.footer_analyses')}</Link>
            <Link to="/secteurs" className="hover:text-white transition-colors">{t('landing.footer_sectors')}</Link>
            <Link to="/blog" className="hover:text-white transition-colors">{t('landing.footer_news')}</Link>
            <Link to="/guide" className="hover:text-white transition-colors">{t('landing.footer_help')}</Link>
            <Link to="/login" className="hover:text-white transition-colors">{t('landing.footer_login')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, desc, color }) => {
  const colorMap = {
      indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
      emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
      sky: 'bg-sky-50 text-sky-600 border-sky-100',
      blue: 'bg-blue-50 text-blue-600 border-blue-100',
      orange: 'bg-orange-50 text-orange-600 border-orange-100',
      amber: 'bg-amber-50 text-amber-600 border-amber-100',
      purple: 'bg-purple-50 text-purple-600 border-purple-100',
      pink: 'bg-pink-50 text-pink-600 border-pink-100',
      rose: 'bg-rose-50 text-rose-600 border-rose-100',
  };
  
  const themeClasses = colorMap[color] || 'bg-slate-50 text-slate-600 border-slate-100';

  return (
    <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all group flex flex-col h-full">
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${themeClasses}`}>
        <Icon size={28} strokeWidth={2}/>
      </div>
      <h3 className="text-xl font-black mb-3 text-slate-900 leading-tight">{title}</h3>
      <p className="text-slate-500 leading-relaxed text-sm flex-1">{desc}</p>
    </div>
  );
};

export default LandingPage;
